import { Typography, message, Row, Col, Card, Select, DatePicker, Table, Tag, Tooltip, Button, Space } from "antd";
import PageWrapper from "../../utils/PageWrapper";
import PageTitle from "../../utils/PageTitle";
import { Eye, EyeOff, User, Calendar, Filter, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../layout/auth";
import moment from "moment";
import { Link } from "react-router-dom";
import "./Reportes.css";

export default function Reportes() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [selectedNom, setSelectedNom] = useState(null);
  const [filteredVerifications, setFilteredVerifications] = useState([]);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const auth = useAuth();

  useEffect(() => {
    if (selectedQuarter && selectedYear) {
      const getQuarterRange = (quarter) => {
        const startOfQuarter = moment().year(selectedYear).quarter(quarter).startOf("quarter");
        const endOfQuarter = moment().year(selectedYear).quarter(quarter).endOf("quarter");
        return [startOfQuarter, endOfQuarter];
      };
      const [start, end] = getQuarterRange(selectedQuarter);
      setDateRange([start, end]);
    }
  }, [selectedQuarter, selectedYear]);

  const clearAllFilters = () => {
    setDateRange(null);
    setSelectedNom(null);
    setSelectedYear(moment().year());
    setSelectedQuarter(null);
  };

  const handleGenerateReport = () => {
    if (!selectedNom) {
      message.error("Por favor, seleccione una NOM.");
      return;
    }

    let start, end;

    if (dateRange && dateRange[0] && dateRange[1]) {
      start = dateRange[0].format("YYYY-MM-DD");
      end = dateRange[1].format("YYYY-MM-DD");
    } else if (selectedYear && selectedQuarter) {
        const getQuarterRange = (quarter) => {
        const startOfQuarter = moment().year(selectedYear).quarter(quarter).startOf("quarter");
        const endOfQuarter = moment().year(selectedYear).quarter(quarter).endOf("quarter");
        return [startOfQuarter, endOfQuarter];
      };
      const [startOfQuarter, endOfQuarter] = getQuarterRange(selectedQuarter);
      start = startOfQuarter.format("YYYY-MM-DD");
      end = endOfQuarter.format("YYYY-MM-DD");
    } else {
        message.error("Por favor, seleccione un rango de fechas o un trimestre.");
        return;
    }

    const nom = selectedNom.toLowerCase();
    window.open(`${process.env.REACT_APP_API_URL}/reportes/${nom}/${start}/${end}`);
  };

  // Fetch verifications
  useEffect(() => {
    if (!auth.UID || !selectedNom || (!selectedQuarter && !dateRange)) {
      setVerifications([]);
      return;
    }

    setLoading(true);
    const params = { u: auth.UID }; // Always send the authenticated user's ID

    axios
      .get(`${process.env.REACT_APP_API_URL}/verificaciones`, { params })
      .then((res) => {
        setVerifications(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  }, [auth.UID, selectedNom, selectedQuarter, dateRange]);

  

  // Filter verifications
  useEffect(() => {
    if (!selectedNom || (!dateRange && !selectedQuarter)) {
      setFilteredVerifications([]);
      return;
    }

    let filtered = verifications;

    // Filter by Status
    filtered = filtered.filter((v) => v.Status === 3 || v.Status === 4);

    // Filter by date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      const [start, end] = dateRange;
      filtered = filtered.filter((v) => {
        const verificationDate = moment(v.FechaEmision);
        return verificationDate.isBetween(start, end, "day", "[]");
      });
    } else if (selectedQuarter && selectedYear) {
      const getQuarterRange = (quarter) => {
        const startOfQuarter = moment().year(selectedYear).quarter(quarter).startOf("quarter");
        const endOfQuarter = moment().year(selectedYear).quarter(quarter).endOf("quarter");
        return [startOfQuarter, endOfQuarter];
      };
      const [start, end] = getQuarterRange(selectedQuarter);
      filtered = filtered.filter((v) => {
        const verificationDate = moment(v.FechaEmision);
        return verificationDate.isBetween(start, end, "day", "[]");
      });
    }

    // Filter by NOM
    if (selectedNom) {
      filtered = filtered.filter((v) => {
        const nom001 = v.Nom001 === "Sí";
        const nom007 = v.Nom007 === "Sí";
        const nom013 = v.Nom013 === "Sí";

        // Apply rules
        if (selectedNom === "Nom007") {
          return nom007 && !nom001 && !nom013;
        }
        if (selectedNom === "Nom013") {
          return nom013 && !nom001 && !nom007;
        }
        return false;
      });
    }

    setFilteredVerifications(filtered);
  }, [dateRange, selectedNom, verifications, selectedQuarter, selectedYear]);

  // Table columns (copied from VerificationsIndex.js and adapted)
  const getStatusInfo = (status) => {
    const map = {
      1: { color: "default", text: "En proceso" },
      2: { color: "DeepSkyBlue", text: "En revisión" },
      3: { color: "#52c41a", text: "Emitida" },
      4: { color: "#52c41a", text: "Pagada" },
      5: { color: "red", text: "Cancelada" },
    };
    return map[status] || { color: "default", text: "Desconocido" };
  };

  const renderNullable = (value, formatter = (v) => v) => (value === null ? <span className="subtle-text">N/D</span> : formatter(value));

  const columns = [
    {
      title: "Folio",
      dataIndex: "ControlInterno",
      key: "ControlInterno",
      width: 140,
      fixed: "left",
      render: (text, record) => {
        const info = getStatusInfo(record.Status);
        if (auth.Rol === "administrador") return text;
        if (record.VerificacionID) return <Link to={`/verificaciones/${record.VerificacionID}`}>{text}</Link>;
        return (
          <>
            <span>Desconocido</span>
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 120,
      render: (status) => {
        const info = getStatusInfo(status);
        return (
          <Tag color={info.color} style={{ fontSize: 12, marginTop: 4 }}>
            {info.text}
          </Tag>
        );
      },
    },
    {
      title: "Verificador",
      dataIndex: "Verificador",
      key: "Verificador",
    },
    {
      title: "NOM",
      key: "noms",
      render: (record) => {
        if (record.Nom007 === "Sí" && record.Nom013 === "Sí") return <span style={{ color: "red" }}>Normas mal aplicadas</span>;
        if (record.Nom007 === "Sí") return "NOM-007";
        if (record.Nom013 === "Sí") return "NOM-013";
        return "";
      },
    },
    {
      title: "Fecha Emision",
      dataIndex: "FechaEmision",
      key: "FechaEmision",
      responsive: ["md"],
      render: (text) => renderNullable(text, (t) => moment(t, "YYYY-MM-DD").format("DD/MM/YYYY")),
    },
    {
      title: "Razón Social",
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
      responsive: ["lg"],
      render: (text) =>
        renderNullable(text, (t) => {
          const short = t.length > 50 ? `${t.slice(0, 20)}...` : t;
          return (
            <Tooltip title={t}>
              <span className="break-by-word">{short}</span>
            </Tooltip>
          );
        }),
    },
  ];

  return (
    <div className="reportes-view-container">
      <PageWrapper className="reportes-card">
        <div className="reportes-controls">
          <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
            <Col>
              <PageTitle icon={<FileText />} title="Reportes de Verificaciones" />
            </Col>
            <Col>
              <Button type="primary" icon={<FileText size={18} />} disabled={filteredVerifications.length === 0} onClick={handleGenerateReport}>
                Generar Reporte
              </Button>
            </Col>
          </Row>

          {/* Filters Legend */}
          <div className="calendar-legend">
            <Row gutter={16} align="middle" justify="space-between" style={{ marginBottom: 16 }}>
              <Col>
                <div className="filters-section">
                  <Space size="small">
                    <h3 className="filters-title">Filtros</h3>
                    <Button type="text" size="small" icon={<Eye size={14} style={{ marginRight: 4 }} />} onClick={clearAllFilters}>
                      Limpiar filtros
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col>
                <div className="filter-item">
                  <Filter size={16} className="filter-icon" />
                  <span className="filter-label">NOM:</span>
                  <Select value={selectedNom} onChange={(value) => setSelectedNom(value)} style={{ width: 150 }} size="small" placeholder="Seleccionar NOM" allowClear>
                    <Select.Option value="Nom007">NOM 007</Select.Option>
                    <Select.Option value="Nom013">NOM 013</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col>
                <div className="filter-item">
                  <Calendar size={16} className="filter-icon" />
                  <span className="filter-label">Año:</span>
                  <Select value={selectedYear} onChange={(value) => setSelectedYear(value)} style={{ width: 100 }} size="small">
                    {[...Array(10).keys()].map((i) => {
                      const year = moment().year() - i;
                      return (
                        <Select.Option key={year} value={year}>
                          {year}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              </Col>
              <Col>
                <div className="filter-item">
                  <Calendar size={16} className="filter-icon" />
                  <span className="filter-label">Trimestre:</span>
                  <Select
                    value={selectedQuarter}
                    onChange={(value) => {
                      setSelectedQuarter(value);
                      if (!value) {
                        setDateRange(null);
                      }
                    }}
                    style={{ width: 150 }}
                    placeholder="Seleccionar"
                    allowClear
                    size="small"
                  >
                    <Select.Option value={1}>Trimestre 1</Select.Option>
                    <Select.Option value={2}>Trimestre 2</Select.Option>
                    <Select.Option value={3}>Trimestre 3</Select.Option>
                    <Select.Option value={4}>Trimestre 4</Select.Option>
                  </Select>
                </div>
              </Col>
              <Col>
                <div className="filter-item">
                  <Calendar size={16} className="filter-icon" />
                  <span className="filter-label">Rango:</span>
                  <DatePicker.RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                      setDateRange(dates);
                      setSelectedQuarter(null);
                    }}
                    size="small"
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Results Table */}
        <div style={{ marginTop: "24px" }}>
          <Table
            columns={columns}
            dataSource={filteredVerifications}
            loading={loading}
            rowKey="VerificacionID"
            scroll={{
              x: "max-content",
              y: "calc(100vh - 400px)",
            }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} verificaciones`,
            }}
          />
        </div>
      </PageWrapper>
    </div>
  );
}
